import { Fragment, ReactNode } from "react";

import {
    Column,
    JLP_LOGO,
    MARGIN_FI_LOGO,
    RewardImage,
    Row,
    StatProps,
    StatWithIcon,
    Text,
    METEORA_SVG,
    Icon,
    useAppPalette,
    SpanFlex,
    SOLAYER_LOGO,
    ADRASTEA_LOGO,
    TextVariant,
    TextColor,
    MEDIA,
    RATEX_LOGO,
    FLASH_LOGO,
    useTextColorMap
} from "@bridgesplit/ui";
import {
    PointsInfo,
    LoopscalePointsAction,
    ExternalPointSource,
    POINTS_SOURCE_INFO,
    ExternalPointReward,
    useLoanMultipliersUtil,
    ParsedVaultRewardsScheduleExpanded,
    isRewardsActive,
    BsMetaUtil,
    LendingVaultExpanded,
    convertExternalRewardToApy,
    getLendingVaultMints,
    getVaultLpVirtualPrice,
    useLatestCollateralYieldHistoryByMint,
    useOraclePrices
} from "@bridgesplit/abf-react";
import { BoltOutlined, LayersOutlined } from "@mui/icons-material";
import { combineCollections, filterTruthy, formatNum, formatPercent, getUnixTs } from "@bridgesplit/utils";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { useMediaQuery } from "@mui/material";
import { TokenImage } from "app/components/common";

import { getExternalPointsRewardIcon } from "./external-point-rewards/ExternalPointReward";
import { useRewardsColor } from "./util";

export interface CollateralInfo {
    pointSource: ExternalPointSource[];
    collateralPointBonus: Map<LoopscalePointsAction, number>;
    name: string;
}

interface TooltipContentProps {
    children: React.ReactNode;
    width?: string;
    px?: number;
    py?: number;
    spacing?: number;
}
export function TooltipContent({
    children,
    width = "250px",
    px = 0,
    py = 0,
    spacing = 1
}: TooltipContentProps): JSX.Element {
    return (
        <Column spacing={spacing} py={py} px={px} sx={{ width }}>
            {children}
        </Column>
    );
}

export function useCreateMultiplierLoops(multipliers: PointsInfo[]): StatProps[] {
    return multipliers.map((multiplier) => {
        return {
            value: <StatWithIcon value={`${formatNum(multiplier.multiplier ?? 1)}x`} />,
            caption: multiplier.caption
        };
    });
}

export function useCreateCollateralStats(multipliers: PointsInfo[] | undefined, isBoosted: boolean) {
    const { prime, secondary } = useAppPalette();
    if (!multipliers) return undefined;
    return multipliers.map((multiplier) => {
        return {
            value: (
                <StatWithIcon
                    value={`${formatNum(multiplier.multiplier ?? 1)}x`}
                    icon={
                        isBoosted ? (
                            <Text sx={{ color: isBoosted ? prime : secondary }}>
                                <BoltOutlined />
                            </Text>
                        ) : undefined
                    }
                />
            ),
            caption: multiplier.caption
        };
    });
}

export function getIconForSource(source: ExternalPointSource): ReactNode {
    switch (source) {
        case ExternalPointSource.MarginFi:
            return <RewardImage src={MARGIN_FI_LOGO} />;
        case ExternalPointSource.Loopscale:
            return <Icon type="points" />;
        case ExternalPointSource.Jupiter:
            return <RewardImage src={JLP_LOGO} />;
        case ExternalPointSource.Meteora:
            return <RewardImage src={METEORA_SVG} />;
        case ExternalPointSource.Solayer:
            return <RewardImage src={SOLAYER_LOGO} />;
        case ExternalPointSource.Adrastea:
            return <RewardImage src={ADRASTEA_LOGO} />;
        case ExternalPointSource.Flash:
            return <RewardImage src={FLASH_LOGO} />;
        case ExternalPointSource.RateX:
            return <RewardImage src={RATEX_LOGO} />;
    }
}

interface PointSourceMapProps {
    pointSources: ExternalPointSource[];
    background?: string;
    isLoading?: boolean;
}

export function PointSourceMap({ pointSources, background, isLoading }: PointSourceMapProps): JSX.Element {
    return (
        <Row spacing={1}>
            {pointSources.map((point) => (
                <Fragment key={point}>
                    <PointSourceIcon source={point} background={background} isLoading={isLoading} />
                </Fragment>
            ))}
        </Row>
    );
}
interface PointSourceIconProps {
    source: ExternalPointSource;
    background?: string;
    isLoading?: boolean;
}

function PointSourceIcon({ source, background }: PointSourceIconProps): JSX.Element {
    if (source === ExternalPointSource.Loopscale) {
        return (
            <Text sx={{ color: background }} variant="body1">
                {getIconForSource(POINTS_SOURCE_INFO[source].externalPointSource)}
            </Text>
        );
    }

    return <SpanFlex>{getIconForSource(POINTS_SOURCE_INFO[source].externalPointSource)}</SpanFlex>;
}

// New common components

export function CaptionWithIcon({
    caption,
    icon,
    variant = "body1",
    iconColor = "caption"
}: {
    caption: string;
    variant?: TextVariant;
    icon: React.ReactNode;
    iconColor?: string;
}) {
    return (
        <Row spacing={1}>
            <Text sx={{ color: iconColor }} variant="body1">
                {icon}
            </Text>
            <Text variant={variant} color="caption">
                {caption}
            </Text>
        </Row>
    );
}

export function ExternalRewardsDisplay({
    metadata,
    collateralMetadata,
    loopscalePointsAction,
    externalPointRewards,
    externalRewardsVault
}: {
    metadata: TokenListMetadata | undefined;
    collateralMetadata?: TokenListMetadata | undefined;
    loopscalePointsAction: LoopscalePointsAction;
    externalPointRewards?: ExternalPointReward[] | undefined;
    externalRewardsVault?: ParsedVaultRewardsScheduleExpanded[] | undefined;
}) {
    const { loanMultipliers, isLoading } = useLoanMultipliersUtil();
    const color = useRewardsColor(loanMultipliers, [metadata?.mint, collateralMetadata?.mint].filter(filterTruthy), [
        loopscalePointsAction
    ]);

    if (isLoading) return null;

    const activeRewards = externalRewardsVault?.filter((reward) => isRewardsActive(reward));

    return (
        <Row spacing={0.5}>
            {activeRewards?.map((reward) => (
                <TokenImage metadata={reward.rewardMetadata} size="sm" key={reward.rewardMetadata.mint} />
            ))}
            {!!externalPointRewards?.length && (
                <Text color="caption">
                    {externalPointRewards?.map((reward, index) => (
                        <Fragment key={`external-reward-${index}`}>{getExternalPointsRewardIcon(reward)}</Fragment>
                    ))}
                </Text>
            )}
            <Text sx={{ color }}>
                <Icon type="points" />
            </Text>
        </Row>
    );
}

export function RateDisplay({
    apy,
    variant = "body1",
    color = "body",
    isFeatured,
    underline = true
}: {
    apy: number | undefined;
    variant?: TextVariant;
    color?: TextColor;
    isFeatured?: boolean;
    underline?: boolean;
}) {
    const customDecimals = 2;
    const maxPrecisionUi = 0.1;

    const fixedWidth = "5ch";
    const isMobile = useMediaQuery(MEDIA.LG.below);

    if (isFeatured || isMobile) {
        return (
            <Row>
                <Text svgColor="disabled" variant={variant} underline={underline} color={color}>
                    {Array.isArray(apy)
                        ? `${formatPercent(apy[0], { customDecimals, maxPrecisionUi })} → ${formatPercent(apy[1], {
                              customDecimals,
                              maxPrecisionUi
                          })}`
                        : formatPercent(apy, { customDecimals, maxPrecisionUi })}
                </Text>
            </Row>
        );
    }

    return (
        <Row sx={{ minWidth: "max-content" }}>
            <Text
                svgColor="disabled"
                variant={variant}
                underline={underline}
                color={color}
                sx={{ display: "inline", width: fixedWidth, justifyContent: "right" }}
            >
                {formatPercent(apy, { customDecimals, maxPrecisionUi })}
            </Text>
        </Row>
    );
}

//Lend Vault Additional Stats
export function useLendVaultAdditionalStats(lendingVaultExpanded: LendingVaultExpanded | undefined): StatProps[] {
    const collateralYieldStats = useCollateralYieldStats(lendingVaultExpanded);
    const externalRewardStats = useExternalRewardStats({ lendingVaultExpanded });
    return combineCollections([collateralYieldStats, externalRewardStats]);
}

export function useCollateralYieldStats(lendingVaultExpanded: LendingVaultExpanded | undefined): StatProps[] {
    const principalMint = lendingVaultExpanded?.vault.principalMint;
    const { data: collateralYield } = useLatestCollateralYieldHistoryByMint(principalMint ? [principalMint] : []);

    const textColorMap = useTextColorMap();
    if (!collateralYield || !lendingVaultExpanded || !principalMint || !collateralYield[principalMint]) return [];
    const symbol = BsMetaUtil.getSymbol(lendingVaultExpanded?.principalMetadata);

    return [
        {
            value: <Text variant="body2">{formatPercent(collateralYield[principalMint])}</Text>,
            caption: (
                <CaptionWithIcon
                    variant="body2"
                    caption={`${symbol} APY`}
                    iconColor={textColorMap("caption")}
                    icon={<LayersOutlined />}
                />
            )
        }
    ];
}

export function useExternalRewardStats({
    lendingVaultExpanded,
    depositAmount = 0
}: {
    lendingVaultExpanded: LendingVaultExpanded | undefined;
    depositAmount?: number;
}): StatProps[] {
    const rewardMintsWithPrincipal = getLendingVaultMints(lendingVaultExpanded);
    const { getUsdPrice } = useOraclePrices(rewardMintsWithPrincipal);
    if (!lendingVaultExpanded) return [];
    const now = getUnixTs();
    const lpPrice = getVaultLpVirtualPrice(lendingVaultExpanded, now);
    const principalPrice = getUsdPrice(lendingVaultExpanded?.vault.principalMint);
    const lpSupply = lendingVaultExpanded.vault.lpSupply;
    const activeRewards = lendingVaultExpanded.vaultRewardsSchedules.filter((schedule) => isRewardsActive(schedule));
    return activeRewards.map((schedule) =>
        createExternalRewardStat(
            schedule,
            lpPrice,
            principalPrice,
            lpSupply,
            getUsdPrice(schedule.rewardMetadata.mint) ?? 0,
            depositAmount
        )
    );
}

function createExternalRewardStat(
    schedule: ParsedVaultRewardsScheduleExpanded,
    lpPrice: number | undefined,
    principalPrice: number | undefined,
    lpSupply: number,
    rewardTokenPrice: number,
    depositAmount = 0
): StatProps {
    const apy = convertExternalRewardToApy(
        schedule,
        lpPrice,
        principalPrice,
        lpSupply,
        rewardTokenPrice,
        depositAmount
    );
    const symbol = BsMetaUtil.getSymbol(schedule.rewardMetadata);

    return {
        value: (
            <Text color="success" variant="body2">
                +{formatPercent(apy)}
            </Text>
        ),
        caption: (
            <CaptionWithIcon
                variant="body2"
                caption={symbol}
                icon={<TokenImage metadata={schedule.rewardMetadata} size="sm" />}
            />
        )
    };
}

export function getCollateralYieldStat(
    collateralYield: number | undefined,
    metadata: TokenListMetadata | undefined,
    textColorMap: ReturnType<typeof useTextColorMap>
): StatProps[] {
    if (!collateralYield || collateralYield === 0 || !metadata) return [];
    const symbol = BsMetaUtil.getSymbol(metadata);
    return [
        {
            value: <Text variant="body2">{formatPercent(collateralYield)}</Text>,
            caption: (
                <CaptionWithIcon
                    variant="body2"
                    caption={`${symbol} APY`}
                    iconColor={textColorMap("caption")}
                    icon={<LayersOutlined />}
                />
            )
        }
    ];
}
